/* eslint-disable */

import dayjs from "dayjs";
import dayjs_plugin_timezone from "dayjs/plugin/timezone";
import dayjs_plugin_utc from "dayjs/plugin/utc";

import CDLWS from "./cdl_ws.js";

let instance = await CDLWS();

// Function to cleanup WebAssembly instance and free memory
export const cleanupCDLInstance = () => {
  if (instance && instance._free) {
    try {
      // Note: This would need to be called when the app is shutting down
      // or when CDL processing is no longer needed
      instance._free();
      instance = null;
    } catch (error) {
      console.warn("Error cleaning up CDL instance:", error);
    }
  }
};

try {
  dayjs.extend(dayjs_plugin_utc);
  dayjs.extend(dayjs_plugin_timezone);
} catch (e) {
  console.log("error extending dayjs", e);
}

/* Input: base64-encoded string containing CDL timestamps
 * Output: array of Date elements
 */
export const decode_cdl_timestamps = async (b64Ts: string) => {
  const fetchUrl = "data:application/octet-binary;base64," + b64Ts;
  const rawFetch = await fetch(fetchUrl);
  const rawTs = await rawFetch.arrayBuffer();
  const rawTsU8 = new Uint8Array(rawTs);

  let ptrIn = null;
  let ptrOut = null;

  try {
    ptrIn = instance._malloc(rawTsU8.length);
    instance.HEAP8.set(rawTsU8, ptrIn);
    ptrOut = instance._cdl_decode_ts(ptrIn, rawTsU8.length);

    if (!ptrOut) {
      throw new Error("TS conversion failed.");
    }

    const nTsOut = instance.HEAPF64[ptrOut / Float64Array.BYTES_PER_ELEMENT];

    var outArr = [];
    for (var i = 0; i < nTsOut; i++) {
      outArr.push(
        new Date(
          instance.HEAPF64[ptrOut / Float64Array.BYTES_PER_ELEMENT + i + 1]
        )
      );
    }

    return outArr;
  } finally {
    // Always free memory, even if there's an error
    if (ptrIn !== null) {
      try {
        instance._free(ptrIn);
      } catch (e) {
        /* ignore */
      }
    }
    if (ptrOut !== null) {
      try {
        instance._free(ptrOut);
      } catch (e) {
        /* ignore */
      }
    }
  }
};

const cdl_types = {
  CCS_TYPE_NONE: "0",
  CCS_TYPE_FLOAT: "1",
  CCS_TYPE_UINT32: "2",
  CCS_TYPE_UINT64: "3",
  CCS_TYPE_FLOAT_ARR_ROW: "4",
  CCS_TYPE_FLOAT_ARR_COL: "5",
  CCS_TYPE_STRING_FIXLEN: "6",
  CCS_TYPE_MAX: "7",
};

const processCDLData: IProcessCDLData = async (data) => {
  let cols_out: Partial<AwaitableReturn<IProcessCDLData>> = {};
  const y = data;
  for (const a of Object.entries(y)) {
    for (const b of Object.entries(a[1])) {
      // check for non object should be here
      const item = y[a[0]][b[0]];

      // eslint-disable-next-line no-continue
      if (typeof item !== "object") continue;

      if (!cols_out[a[0]]?.[b[0]]) {
        const arrBuff = await fetch(
          `data:application/octet-stream;base64,${item.data}`
        );
        const arrBuffRes = await arrBuff.arrayBuffer();

        let parsedData = null;

        if (item.type === cdl_types.CCS_TYPE_FLOAT) {
          parsedData = Float64Array.from(new Float32Array(arrBuffRes));
        }

        if (item.type === cdl_types.CCS_TYPE_UINT32) {
          parsedData = Float64Array.from(new Uint32Array(arrBuffRes));
        }

        if (item.type === cdl_types.CCS_TYPE_UINT64) {
          const bigarr = new BigUint64Array(arrBuffRes);
          parsedData = [];
          for (const i in bigarr) {
            parsedData.push(Number(bigarr[i]));
          }
        }

        if (item.type === cdl_types.CCS_TYPE_MAX) {
          const parsedDataTimeStamps = await decode_cdl_timestamps(item.data);
          parsedData = parsedDataTimeStamps.map((item) => item.getTime());
        }

        // Disabled
        // eslint-disable-next-line no-constant-condition
        // if (item.type === cdl_types.CCS_TYPE_FLOAT_ARR_COL) {
        //   const col_flat = new Float32Array(arrBuffRes);
        //   parsedData = [];

        //   /*
        //   This operation is filling up system memory and leaving system
        //   inoperable. This should be done by vectorized operation using numpy
        //   or other optimized method.
        //   */
        //   for (let r = 0; r < 16000; r++) {
        //     for (let i = 0; i < a[1].n_rows; i++) {
        //       if (!(i in parsedData)) parsedData[i] = [];
        //       parsedData[i][r] = Math.log10(col_flat[r * a[1].row_len + i]);
        //     }
        //   }
        // }

        cols_out = {
          ...cols_out,
          [a[0]]: {
            ...cols_out[a[0]],
            [b[0]]: { type: item.type, arr: parsedData },
          },
        };
      }
    }
  }

  // Optimize idx array size based on actual data processed instead of fixed 160k allocation
  // Calculate the maximum data length from all processed items
  let maxDataLength = 0;
  for (const [key, value] of Object.entries(cols_out)) {
    if (key !== "idx" && typeof value === "object") {
      for (const [subKey, subValue] of Object.entries(value)) {
        if (
          subValue &&
          typeof subValue === "object" &&
          "arr" in subValue &&
          subValue.arr
        ) {
          maxDataLength = Math.max(maxDataLength, subValue.arr.length);
        }
      }
    }
  }

  // Use actual data size or minimum of 100 elements, whichever is larger
  const idxSize = Math.max(maxDataLength, 100);
  const idxArr: Array<string | undefined> = [];
  idxArr.length = idxSize;
  cols_out.idx = idxArr;

  // Memory optimization: Reduced idx array from 160k to actual data size

  return cols_out as AwaitableReturn<IProcessCDLData>;
};

const formatMotion: IFormatMotion = (data, key, extractKey) => {
  const newData: ReturnType<IFormatMotion> = [];
  const timestampKey = extractKey.includes("hr_3s")
    ? "timestamp_hr"
    : "timestamp_rr";

  const keyedData = data[key][extractKey].arr || [];
  const tsKeyedData = data[key][timestampKey].arr || [];

  keyedData.forEach((item, index) => {
    newData.push({
      value: Number(item),
      dt: tsKeyedData[index] ? parseInt(tsKeyedData[index].toString(), 10) : 0,
    });
  });

  return newData;
};

const formatHourly3DData = (
  data: AwaitableReturn<IProcessCDLData>,
  key: string,
  extractKey: string,
  timezone: string
) => {
  const newData: {
    quant2: string | number;
    dt: string;
    nr: string | number;
  }[] = [];
  const timestampKey = extractKey.includes("hr_3s")
    ? "timestamp_hr"
    : "timestamp_rr";

  if (data[key]) {
    data[key][extractKey]!!.arr!!.forEach((item, index) => {
      newData.push({
        quant2: item,
        dt: dayjs(data[key][timestampKey].arr!![index])
          .tz(timezone)
          .format("YYYY-MM-DD HH:mm:00"),
        nr: data[key].count.arr!![index],
      });
      return item;
    });
  }
  return newData;
};

const formatDaily3DData = (
  data: AwaitableReturn<IProcessCDLData>,
  key: string,
  extractKey: string,
  timezone: string
) => {
  const newData: {
    quant2: string | number;
    dt: string;
    nr: string | number;
  }[] = [];
  const timestampKey = extractKey.includes("hr_3s")
    ? "timestamp_hr"
    : "timestamp_rr";

  if (data[key]) {
    data[key][extractKey]!!.arr!!.forEach((item, index) => {
      newData.push({
        quant2: item,
        dt: dayjs(data[key][timestampKey].arr!![index])
          .tz(timezone)
          .format("YYYY-MM-DD 00:00:00"),
        nr: data[key].count.arr!![index],
      });
      return item;
    });
  }
  return newData;
};

export const processDailyData = async (data: any) => {
  const newData = [];

  for (let i = 0; i < data.length; i++) {
    let item = data[i];
    const processedData = await processCDLData(item.data);
    const formattedData = formatDailyData(
      processedData,
      "rr_3s.ds_dly",
      "rr_3s",
      item.data.timezone
    );

    const daysRange: Dictionary<number> = {};

    generateDateRange(
      dayjs().subtract(6, "days").format("YYYY-MM-DDT00:00:00Z")!!,
      dayjs().format("YYYY-MM-DDT23:59:59Z")!!
    ).forEach((item) => {
      daysRange[item] = 0;
    });
    formattedData.forEach((item) => {
      daysRange[item.dt] = +item.quant2;
    });

    newData.push({
      patientId: item.patientId,
      data: daysRange,
    });
  }

  return newData;
};

export const processHourlyData = async (data: any) => {
  const newData = [];

  for (let i = 0; i < data.length; i++) {
    let item = data[i];

    const processedData = await processCDLData(item.data);

    const formattedData = formatHourly3DData(
      processedData,
      "rr_3s.ds_hrly",
      "rr_3s",
      item.data.timezone
    );

    const timeRange: Dictionary<number> = {};

    formattedData.forEach((item) => {
      timeRange[item.dt] = +item.quant2;
    });

    newData.push({
      patientId: item.patientId,
      data: timeRange,
    });
  }

  return newData;
};

export const processHourlyDataHR = async (data: any) => {
  const newData = [];

  for (let i = 0; i < data.length; i++) {
    let item = data[i];
    const processedData = await processCDLData(item.data);

    const formattedData = formatHourly3DData(
      processedData,
      "hr_3s.ds_hrly",
      "hr_3s",
      item.data.timezone
    );

    const timeRange: Dictionary<number> = {};

    formattedData.forEach((item) => {
      timeRange[item.dt] = +item.quant2;
    });

    newData.push({
      patientId: item.patientId,
      data: timeRange,
    });
  }

  return newData;
};

// self.onmessage = async (e) => {
//   const { data } = e;
//   const { action, data: subData, actionId, patientId } = data;
//   switch (action) {
//     case "processCDLData":
//       try {
//         const processedData = await processCDLData(subData);
//         self.postMessage({
//           action: "processCDLData",
//           data: processedData,
//           actionId,
//         });
//       } catch (e) {
//         console.log("error on processCDLData", e);
//         self.postMessage({
//           action: "processCDLData",
//           data: [],
//           error: e,
//           actionId,
//         });
//       }
//       break;
//     case "formatMotion":
//       try {
//         const processedData = await processCDLData(subData);
//         const formattedData = formatMotion(
//           processedData,
//           "mvmt_in_range.ds_min.thr(>10000).ds_hour_sum.agg_hourly_mean",
//           "mvmt_in_range"
//         );
//         self.postMessage({
//           action: "formatMotion",
//           data: formattedData,
//           actionId,
//         });
//       } catch (e) {
//         console.log(" error on formatMotion", e);
//         self.postMessage({
//           action: "formatMotion",
//           data: [],
//           error: e,
//           actionId,
//         });
//       }
//       break;
//     case "formatBedExit":
//       try {
//         const processedData = await processCDLData(subData);
//         const formattedData = formatMotion(
//           processedData,
//           "bed_exit_prob.ma(5).thr(>0.65).cooldown(40).ds_hour_sum.agg_hourly_mean",
//           "bed_exit_prob"
//         );
//         self.postMessage({
//           action: "formatBedExit",
//           data: formattedData,
//           actionId,
//         });
//       } catch (e) {
//         console.log(" error on formatBedExit", e);
//         self.postMessage({
//           action: "formatBedExit",
//           data: [],
//           error: e,
//           actionId,
//         });
//       }
//       break;
//     case "formatHourly3DData":
//       try {
//         const processedData = await processCDLData(subData);
//         const formattedData = formatHourly3DData(
//           processedData,
//           "rr_3s.ds_hrly",
//           "rr_3s",
//           subData.timezone
//         );
//         self.postMessage({
//           action: "formatHourly3DData",
//           data: formattedData,
//           actionId,
//         });
//       } catch (e) {
//         console.log(" error on formatHourly3DData", e);
//         self.postMessage({
//           action: "formatHourly3DData",
//           data: [],
//           error: e,
//           actionId,
//         });
//       }
//       break;
//     case "formatDaily3DData":
//       try {
//         const processedData = await processCDLData(subData);
//         const formattedData = formatDaily3DData(
//           processedData,
//           "rr_3s.ds_dly",
//           "rr_3s",
//           subData.timezone
//         );
//         self.postMessage({
//           action: "formatDaily3DData",
//           data: formattedData,
//           actionId,
//         });
//       } catch (e) {
//         console.log(" error on formatDaily3DData", e);
//         self.postMessage({
//           action: "formatDaily3DData",
//           data: [],
//           error: e,
//           actionId,
//         });
//       }

//       break;
//     case "formatDailyData":
//       try {
//         const newData = [];

//         for (let i = 0; i < subData.length; i++) {
//           let item = subData[i];
//           const processedData = await processCDLData(item.data);
//           const formattedData = formatDailyData(
//             processedData,
//             "rr_3s.ds_dly",
//             "rr_3s",
//             item.data.timezone
//           );

//           const daysRange: Dictionary<number> = {};

//           generateDateRange(
//             dayjs().subtract(6, "days").format("YYYY-MM-DDT00:00:00Z")!!,
//             dayjs().format("YYYY-MM-DDT23:59:59Z")!!
//           ).forEach((item) => {
//             daysRange[item] = 0;
//           });
//           formattedData.forEach((item) => {
//             daysRange[item.dt] = +item.quant2;
//           });

//           newData.push({
//             patientId: item.patientId,
//             data: daysRange,
//           });
//         }

//         self.postMessage({
//           action: "formatDailyData",
//           data: newData,
//           patientId,
//           actionId,
//         });
//       } catch (e) {
//         console.log(" error on formatDailyData", e);
//         self.postMessage({
//           action: "formatDailyData",
//           data: [],
//           error: e,
//           actionId,
//         });
//       }

//       break;
//     default:
//       self.postMessage({
//         action: "error",
//         data: "Invalid action",
//         actionId,
//       });

//       break;
//   }
// };

// TODO - Fawwad : Remove the above function if this works perfectly
const formatDailyData = (
  data: AwaitableReturn<IProcessCDLData>,
  key: string,
  extractKey: string,
  timezone: string
) => {
  const newData: {
    quant2: string | number;
    dt: string;
    nr: string | number;
  }[] = [];
  const timestampKey = extractKey.includes("hr_3s")
    ? "timestamp_hr"
    : "timestamp_rr";

  if (data[key]) {
    data[key][extractKey]!!.arr!!.forEach((item, index) => {
      newData.push({
        quant2: item,
        dt: dayjs(data[key][timestampKey].arr!![index])
          .tz(timezone)
          .format("YYYY-MM-DD 00:00:00"),
        nr: data[key].count.arr!![index],
      });
      return item;
    });
  }
  return newData;
};

function generateDateRange(
  startDateString: string,
  endDateString: string
): string[] {
  const startDate = dayjs(startDateString);
  const endDate = dayjs(endDateString);
  const dateRange: string[] = [];

  if (endDate.isBefore(startDate)) {
    throw new Error("End date must be greater than or equal to start date.");
  }

  let currentDate = startDate;
  while (currentDate.isSame(endDate) || currentDate.isBefore(endDate)) {
    dateRange.push(currentDate.format("YYYY-MM-DD 00:00:00"));
    currentDate = currentDate.add(1, "day");
  }

  return dateRange;
}
